import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import type { ClientSecurityInfo } from '../tracking-demo.models';
import { launchSourceLabel } from '../tracking-format.util';

/** Reusable key/value grid rendering a participant's captured security + device details. */
@Component({
  selector: 'app-security-info',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    @if (!info) {
      <p class="text-xs text-gray-400 italic">No security details captured for this participant.</p>
    } @else {
      <div class="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
        @for (row of rows(); track row.label) {
          <div class="flex flex-col">
            <span class="text-[10px] uppercase tracking-wide text-gray-400">{{ row.label }}</span>
            <span class="text-gray-700 dark:text-gray-200 break-words">{{ row.value || '—' }}</span>
          </div>
        }
      </div>
      @if (info.userAgent) {
        <div class="mt-3">
          <span class="text-[10px] uppercase tracking-wide text-gray-400">User Agent</span>
          <p class="text-[11px] text-gray-500 dark:text-gray-400 break-all">{{ info.userAgent }}</p>
        </div>
      }
    }
  `,
})
export class SecurityInfoComponent {
  @Input() info: ClientSecurityInfo | null = null;

  rows(): { label: string; value: string | null }[] {
    const i = this.info;
    if (!i) return [];
    const loc = [i.city, i.state, i.country].filter(Boolean).join(', ') || null;
    const geo = i.latitude != null && i.longitude != null ? `${i.latitude}, ${i.longitude}` : null;
    // networkType is an effectiveType bucket ('4g') and connectionQuality is an estimated
    // downlink in Mbps — carry the unit so the pair doesn't read as one opaque number.
    const network = [i.networkType, i.connectionQuality ? `${i.connectionQuality} Mbps` : null]
      .filter(Boolean).join(' · ') || null;
    return [
      { label: 'IP Address', value: i.ip },
      { label: 'Location', value: loc },
      { label: 'Coordinates', value: geo },
      { label: 'Timezone', value: i.timezone },
      { label: 'ISP', value: i.isp },
      { label: 'Browser', value: [i.browser, i.browserVersion].filter(Boolean).join(' ') || null },
      { label: 'Operating System', value: [i.os, i.osVersion].filter(Boolean).join(' ') || null },
      { label: 'Device', value: i.deviceType },
      // Declared by the entry link (a native app's `?device_type=`), not sniffed —
      // shown even when it disagrees with Device, because that disagreement is
      // exactly what an admin reviewing a session wants to see.
      { label: 'Launched from', value: launchSourceLabel(i) },
      { label: 'Screen', value: i.screenResolution },
      { label: 'Language', value: i.language },
      { label: 'Platform', value: i.platform },
      { label: 'Network', value: network },
    ];
  }
}
